import React, { useRef, useState, useEffect, useMemo } from 'react';
import '@lottiefiles/lottie-player';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NoBgRoundedBlackBorderLight from '../../../components2/Buttons/NoBgRoundedBlackBorderLight';
import SearchItems from '../../../components2/SearchItems';
import SearchIcon from '../../../static/img/search14gray.svg';
import SelectAllIcon from '../../../static/icons/select-all-icon.svg';
import DeleteIcon from '../../../static/icons/blue-delete-icon.svg';
import HideIcon from '../../../static/icons/hide-icon.svg';
import TopIcon from '../../../static/icons/top-icon.svg';
import PlusWhiteIcon from '../../../static/icons/pluswhite.svg';
import UploadIconSrc from '../../../static/icons/share-download-icon.svg';
import ShareBulkIcon from '../../../static/icons/ShareBlue.svg';
import DownloadIcon from '../../../static/icons/DowloadBlue.svg';
import SubjectFilter from '../SubjectFilter';
import SortByControl from '../SortByControl';
import { exportMultipleFilesAsZip, exportJsonFile } from '../../../components2/markingCriteria';
import {
  MainContainer,
  InnerContainer,
  RightContainer,
  RightSideHeader,
  CreateButtonContainer,
  ImportFileLabel,
  RightSideOptions,
  CardImgCont,
  CardImg,
  CardTitle,
  ImportFile,
  UploadIcon,
  PlusText,
  PopUpContainer,
  PopUpCardImg,
  PopUpCard,
  PopUpCardText,
  PlusIconHover,
  HeadingAndFilterContainer,
  BulkActionsContainer,
  BulkActionButton,
  SelectedCountText,
  SearchContainer,
  SearchInputWrapper,
  SearchIconImg,
  DragHandle,
  DragDots,
  DragDot,
  BulkActionIcon,
  SortableItemContainer,
  SortableContentWrapper,
  SortableChildrenContainer,
  MarkingCriteriaList,
  ListAreaWrapper,
  ListLoaderOverlay,
  ListLoaderText,
} from './style';
import ScrollableArea from '../ScrollableArea';
import { useFeedbackToolContext } from './FeedbackToolProvider';
import { useProfile } from '../../state/hooks';
import { useApiSubjects } from '../../../hooks/useSubjects';
import { useMarkingToolStates } from '../../../hooks/useUserStates';
import { computeHiddenSubjectCodes } from '../../../utils/subjects';
import ShareResourcePopup from '../../../components2/ShareResourcePopup';
import ActionPopup from '../../../components2/ActionPopup';
import SharedWithMeLink from '../../../components2/SharedWithMeLink';
import { applySubjectYearFilter as applySubjectYearFilterUtil } from '../../../utils/markingToolsFilter';

const getItemId = (item) => {
  if (!item || typeof item !== 'object') return null;
  if (item.id) return item.id;
  return null;
};

const getItemSortOrder = (item) => {
  if (!item || typeof item !== 'object') return null;
  if (item.sortOrder !== undefined) return item.sortOrder;
  return null;
};

 const getItemTitle = (item) => {
  if (!item || typeof item !== 'object') return '';
  return item.title || '';
};

// Check if item has actual content (not just metadata from classes)
const hasItemContent = (item, contextType) => {
  if (!item || typeof item !== 'object') return false;

  // Check for common content indicators
  const hasTitle = item.title && item.title.trim() !== '';
  const hasDescription = item.description && item.description.trim() !== '';

  if (contextType === 'markingCriteria') {
    // Marking criteria should have criteria/criterias, levels, or bands
    const hasCriteria = (Array.isArray(item.criteria) && item.criteria.length > 0) ||
                        (Array.isArray(item.criterias) && item.criterias.length > 0);
    const hasLevels = Array.isArray(item.levels) && item.levels.length > 0;
    const hasBands = Array.isArray(item.bands) && item.bands.length > 0;
    return hasTitle || hasDescription || hasCriteria || hasLevels || hasBands;
  }

  if (contextType === 'commentBanks') {
    // Comment banks should have comments or smart comments
    const hasComments = Array.isArray(item.comments) && item.comments.length > 0;
    const hasSmartComments = Array.isArray(item.smartComments) && item.smartComments.length > 0;
    const hasAnnotations = Array.isArray(item.annotations) && item.annotations.length > 0;
    return hasTitle || hasDescription || hasComments || hasSmartComments || hasAnnotations;
  }

  if (contextType === 'successCriteria') {
    const hasCriteria = Array.isArray(item.criteria) && item.criteria.length > 0;
    const hasContent = item.content && item.content.trim() !== '';
    return hasTitle || hasDescription || hasCriteria || hasContent;
  }

  if (contextType === 'shortAnswers') {
    const hasAnswers = Array.isArray(item.answers) && item.answers.length > 0;
    const hasContent = item.content && item.content.trim() !== '';
    const hasAnswer = item.answer && item.answer.trim() !== '';
    return hasTitle || hasDescription || hasAnswers || hasContent || hasAnswer;
  }

  // Default: item has content if it has title or description
  return hasTitle || hasDescription;
};

const SortableItem = ({ id, children, disabled, hasActiveFilters, hasSelectedItems }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: id.toString(),
    disabled: disabled
  });

  const isDragHandleDisabled = disabled || hasActiveFilters || hasSelectedItems;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    cursor: isDragHandleDisabled ? 'not-allowed' : 'grab',
    userSelect: 'none',
    zIndex: isDragging ? 1000 : 'auto'
  };

  return (
    <SortableItemContainer
      ref={setNodeRef}
      isDragging={isDragging}
      className={isDragging ? 'dragging' : ''}
      style={style}
    >
      <SortableContentWrapper>
        <DragHandle 
          style={{ 
            opacity: isDragHandleDisabled ? 0.3 : 1,
            cursor: isDragHandleDisabled ? 'not-allowed' : 'grab',
            pointerEvents: isDragHandleDisabled ? 'none' : 'auto',
            userSelect: 'none'
          }}
          {...attributes}
          {...listeners}
        >
          <DragDots>
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
          </DragDots>
        </DragHandle>
        
        <SortableChildrenContainer>
          {children}
        </SortableChildrenContainer>
      </SortableContentWrapper>
    </SortableItemContainer>
  );
};

function FeedbackTool({
  onImport = () => {},
  onDelete = () => {},
  onHide = () => {},
  onShow = () => {},
  onMoveToTop = () => {},
  onReorder = () => {},
  onSelectAll = () => {},
  renderItem = () => {},
  renderCreateOptions = () => {},
  renderImportDialog = () => {},
  renderCreatePopup = null,
  filteredItems = [],
  selectedItems: externalSelectedItems,
  isDragDisabled = false,
  hasActiveFilters = false,
  mutations: externalMutations = {},
  tabletView = false,
  MenuButton = null,
  SecondarySidebar = null,
  customContent = null,
  usePopupForCreate = false,
  showHidden: externalShowHidden,
  setShowHidden: externalSetShowHidden,
  showOnlyMineFilter: externalShowOnlyMineFilter,
  setShowOnlyMineFilter: externalSetShowOnlyMineFilter,
  enableReactQuery = false,
  allMarkingCriteria = [],
  setSelectedCriteria = null,
  hideCreateButton = false,
}) {
  const [isShowMenu, setShowMenu] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareItems, setShareItems] = useState([]);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [filterSelectedYears, setFilterSelectedYears] = useState([]);
  const [filterSelectedSubjects, setFilterSelectedSubjects] = useState([]);
  const [filterSelectedTaskTypes, setFilterSelectedTaskTypes] = useState([]);
  const [filterSelectedResponseTypes, setFilterSelectedResponseTypes] = useState([]);
  
  const divRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  const context = enableReactQuery ? useFeedbackToolContextSafe() : null;

  // Helper to safely get context without throwing error
  function useFeedbackToolContextSafe() {
    try {
      return useFeedbackToolContext();
    } catch {
      // Context not available - return null
      return null;
    }
  }

  const {
    searchValue: contextSearchValue = '',
    showHidden: contextShowHidden = false,
    showOnlyMineFilter: contextShowOnlyMineFilter = false,
    setFilterParams: contextSetFilterParams = () => {},
    queryParams: contextQueryParams = {},
    queries = { items: { data: [], isLoading: false, error: null } },
    mutations: contextMutations = {},
    selectAll = () => {},
    deselectAll = () => {},
    setShowHidden: contextSetShowHidden = () => {},
    setShowOnlyMineFilter: contextSetShowOnlyMineFilter = () => {},
  } = context || {};
  const filterSortOrder = contextQueryParams.sortOrder;
  const isSortActive = filterSortOrder === 'newToOld' || filterSortOrder === 'oldToNew';
  const sortByUiValue = filterSortOrder === 'newToOld' ? 'newestFirst' : filterSortOrder === 'oldToNew' ? 'oldestFirst' : null;
  const handleSortByChange = (uiValue) => {
    if (!contextSetFilterParams) return;
    const apiValue = uiValue === 'newestFirst' ? 'newToOld' : uiValue === 'oldestFirst' ? 'oldToNew' : undefined;
    contextSetFilterParams({ sortOrder: apiValue });
  };

  const { data: profile } = useProfile();
  const { apiSubjects } = useApiSubjects();
  // Individual-teacher state scope (empty for school/stateless users → no scoping).
  const scopeStates = useMarkingToolStates();
  const setFilterParamsRef = useRef(contextSetFilterParams);
  setFilterParamsRef.current = contextSetFilterParams;

  // Subject/year/taskType/responseType filtering is done on the frontend
  // via applySubjectYearFilter, so we don't pass these to the API.

  const getShareTypeForItem = (item) => {
    if (item && item.shareType) {
      return item.shareType;
    }
    if (context?.state?.context === 'commentBanks') {
      return 'commentBank';
    }
    if (context?.state?.context === 'successCriteria') {
      return 'successCriteria';
    }
    if (context?.state?.context === 'shortAnswers') {
      return 'shortAnswer';
    }
    if (context?.state?.context === 'markingCriteria') {
      return 'markingCriteria';
    }
    if (item?.type === 'Comment Bank') {
      return 'commentBank';
    }
    return 'markingCriteria';
  };

  const mutations = Object.keys(externalMutations).length > 0 ? externalMutations : contextMutations;
  
    const selectedItems = (() => {
    if (setSelectedCriteria !== null) {
      return externalSelectedItems || new Set();
    } else if (enableReactQuery && context?.selectedItems) {
      return context.selectedItems;
    } else {
      return externalSelectedItems || new Set();
    }
  })();

  const selectedItemIds = selectedItems instanceof Set
    ? Array.from(selectedItems)
    : Array.isArray(selectedItems)
      ? selectedItems
      : [];

  const searchValue = contextSearchValue || localSearchValue;
  const showHidden = externalShowHidden !== undefined ? externalShowHidden : contextShowHidden;
  const showOnlyMineFilter = externalShowOnlyMineFilter !== undefined ? externalShowOnlyMineFilter : contextShowOnlyMineFilter;

  const hasSelectedItems = selectedItems && selectedItems.size > 0;
  const hasSubjectFilters = filterSelectedYears.length > 0 || filterSelectedSubjects.length > 0
    || filterSelectedTaskTypes.length > 0 || filterSelectedResponseTypes.length > 0;
  const hasInternalActiveFilters = hasActiveFilters || searchValue.trim() !== '' || hasSubjectFilters;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { 
        distance: 0,
        delay: 0,
        tolerance: 5
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getAcceptFileType = () => {
    if (context?.state?.context === 'commentBanks') {
      return '.cb';
    } else if (context?.state?.context === 'successCriteria') {
      return '.sc';
    } else if (context?.state?.context === 'shortAnswers') {
      return '.sa';
    } else {
      return '.mc';
    }
  };

  const getCreateButtonText = () => {
    const ctx = context?.state?.context;
    if (ctx === 'commentBanks') return 'New Comment Bank';
    if (ctx === 'shortAnswers') return 'New Short Answer';
    if (ctx === 'successCriteria') return 'New Success Criteria';
    if (ctx === 'markingCriteria') return 'New Rubric';
    return 'Add New';
  };

  const handleSearchChange = (value) => {
    setLocalSearchValue(value);
  };

  const handleShowHiddenChange = (value) => {
    if (externalSetShowHidden) {
      externalSetShowHidden(value);
    } else {
      contextSetShowHidden(value);
    }
  };

  const handleShowOnlyMineChange = (value) => {
    if (externalSetShowOnlyMineFilter) {
      externalSetShowOnlyMineFilter(value);
    } else {
      contextSetShowOnlyMineFilter(value);
    }
  };

  const handleCreateButtonClick = () => {
    if (usePopupForCreate) {
      setShowCreatePopup(true);
    } else {
      const currentPath = (location.hash && location.hash.startsWith('#/') 
        ? location.hash.substring(1) 
        : location.pathname).split('?')[0].split('#')[0];
      const isRubricsPage = currentPath === '/tools/rubrics' || currentPath.startsWith('/tools/rubrics/');
      const isMarkingCriteriaContext = context?.state?.context === 'markingCriteria';
      
      if (isRubricsPage && isMarkingCriteriaContext) {
        history.push('/tools/rubrics/new');
      } else {
        setOpenCreateDialog(!openCreateDialog);
      }
    }
  };

  const handleDragStart = (event) => {
    if (isDragDisabled || isSortActive) return;
  };

  const handleDragEnd = async (event) => {
    const { active, over, delta } = event;
    if (!active || !over || active.id === over.id || isDragDisabled || isSortActive) return;

    const dragDistance = Math.sqrt(delta.x * delta.x + delta.y * delta.y);
    if (dragDistance < 8) return;

    const draggedItem = itemsArray.find(item => getItemId(item) === active.id);
    const targetItem = itemsArray.find(item => getItemId(item) === over.id);
    
    if (!draggedItem || !targetItem) {
      console.error('Drag Error: Items not found in original array');
      return;
    }

    const oldIndex = itemsArray.findIndex(item => getItemId(item) === active.id);
    const newIndex = itemsArray.findIndex(item => getItemId(item) === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const itemId = getItemId(draggedItem);
      
      if (!itemId) {
        console.error('Drag Error: No valid item ID found');
        return;
      }

      let newSortOrder = newIndex + 1;

      newSortOrder = Math.max(1, newSortOrder);

      if (enableReactQuery && mutations?.reorderItems) {
        try {
          mutations.reorderItems.mutate({ criteriaId: itemId, newSortOrder });
        } catch (error) {
          console.error('Error reordering items:', error);
        }
      } else {
        onReorder({ 
          itemId, 
          newSortOrder, 
          reorderedItems: itemsArray.map(item => item), 
          originalItems: itemsArray
        });
      }
    }
  };

  const itemsArray = (() => {
    // Check if API sortOrder filter is active - if so, preserve API order
    const hasApiSortOrder = filterSortOrder === 'newToOld' || filterSortOrder === 'oldToNew';

    if (enableReactQuery && queries?.items?.data !== undefined && queries?.items?.data !== null) {
      if (context?.state?.context === 'markingCriteria') {
        const { markingCriterias, preferences } = queries.items.data;

        if (markingCriterias && preferences) {
          const preferencesMap = new Map();
          preferences.forEach(pref => {
            const key = pref.markingCriteriaId || pref.id;
            if (key) {
              preferencesMap.set(key, pref);
            }
          });

          const result = markingCriterias.map((criteria, index) => {
            const preference = preferencesMap.get(criteria.id);
            const defaultSortOrder = preference?.sortOrder ?? (1000 + index);
            return {
              ...criteria,
              isVisible: preference?.isVisible ?? true,
              visible: preference?.visible ?? true,
              sortOrder: defaultSortOrder
            };
          });

          // Only sort locally if no API sortOrder filter is active
          if (!hasApiSortOrder) {
            result.sort((a, b) => {
              const aOrder = a.sortOrder ?? 999;
              const bOrder = b.sortOrder ?? 999;
              return aOrder - bOrder;
            });
          }
          return result;
        } else {
          return queries.items.data;
        }
      } else if (context?.state?.context === 'commentBanks') {
        const data = queries.items.data;
        const commentBanks = data?.commentBanks ?? data?._embedded?.commentbanks ?? [];
        if (Array.isArray(commentBanks)) {
          const result = [...commentBanks];
          result.forEach((bank, index) => {
            if (bank.sortOrder === undefined || bank.sortOrder === null) {
              bank.sortOrder = 1000 + index;
            }
          });
          // Only sort locally if no API sortOrder filter is active
          if (!hasApiSortOrder) {
            result.sort((a, b) => {
              const aOrder = a.sortOrder ?? 999;
              const bOrder = b.sortOrder ?? 999;
              return aOrder - bOrder;
            });
          }
          return result;
        }
        return [];
      } else if (context?.state?.context === 'successCriteria') {
        if (queries.items.data && queries.items.data.successCriteria && Array.isArray(queries.items.data.successCriteria)) {
          const result = queries.items.data.successCriteria;
          if (result.length > 0) {
            result.forEach((criteria, index) => {
              if (criteria.sortOrder === undefined || criteria.sortOrder === null) {
                criteria.sortOrder = 1000 + index;
              }
            });
            // Only sort locally if no API sortOrder filter is active
            if (!hasApiSortOrder) {
              result.sort((a, b) => {
                const aOrder = a.sortOrder ?? 999;
                const bOrder = b.sortOrder ?? 999;
                return aOrder - bOrder;
              });
            }
          }
          return result;
        } else {
          return [];
        }
      } else if (context?.state?.context === 'shortAnswers') {
        if (queries.items.data && queries.items.data.shortAnswers && Array.isArray(queries.items.data.shortAnswers)) {
          const result = queries.items.data.shortAnswers;
          if (result.length > 0) {
            result.forEach((answer, index) => {
              if (answer.sortOrder === undefined || answer.sortOrder === null) {
                answer.sortOrder = 1000 + index;
              }
            });
            // Only sort locally if no API sortOrder filter is active
            if (!hasApiSortOrder) {
              result.sort((a, b) => {
                const aOrder = a.sortOrder ?? 999;
                const bOrder = b.sortOrder ?? 999;
                return aOrder - bOrder;
              });
            }
          }
          return result;
        } else {
          return [];
        }
      } else {
        const result = Array.isArray(queries.items.data) ? queries.items.data : [];

        if (result.length > 0) {
          result.forEach((item, index) => {
            if (item.sortOrder === undefined || item.sortOrder === null) {
              item.sortOrder = 1000 + index;
            }
          });
          // Only sort locally if no API sortOrder filter is active
          if (!hasApiSortOrder) {
            result.sort((a, b) => {
              const aOrder = a.sortOrder ?? 999;
              const bOrder = b.sortOrder ?? 999;
              return aOrder - bOrder;
            });
          }
        }
        return result;
      }
    }

    return filteredItems || [];
  })();

  // Use item's actual data for filter options
  // For commentBanks/successCriteria/shortAnswers:
  //   - years are in metadata.years (actual item data)
  //   - subjects are in direct item.subjects property (actual item data)
  //   - taskTypes are in metadata.taskTypes (actual item data)
  //   - responseTypes are in metadata.responseTypes (actual item data)
  //   - metadata.subjects and metadata.subjectGroups are from /classes API - ignore these
  const contextType = context?.state?.context || 'markingCriteria';
  const itemsForFilters = Array.isArray(itemsArray)
    ? itemsArray
        .filter(item => {
          if (!hasItemContent(item, contextType)) return false;
          // Match visibility/ownership filters so filter options reflect current view
          if (enableReactQuery && queries?.items?.data !== undefined) {
            if (showHidden) {
              if (item.isVisible !== false && item.visible !== false) return false;
            } else {
              if (item.isVisible === false || item.visible === false) return false;
            }
            if (showOnlyMineFilter && item.isSystem) return false;
          }
          return true;
        })
        .map(item => {
          const useRootOnly = contextType === 'markingCriteria';
          // For markingCriteria, collect years and subjects from all possible sources
          const allYears = useRootOnly
            ? [
                ...(item.metadata?.years || []),
                ...(item.years || []),
                ...(item.year ? [item.year] : []),
              ].filter(Boolean)
            : (item.metadata?.years || item.years || []);
          const allSubjects = item.metadata?.subjects?.length
            ? item.metadata.subjects
            : (item.subjects || []);
          return {
            ...item,
            years: allYears,
            subjects: allSubjects,
            taskTypes: item.metadata?.taskTypes?.length ? item.metadata.taskTypes : (item.taskTypes?.length ? item.taskTypes : []),
            responseTypes: item.metadata?.responseTypes?.length ? item.metadata.responseTypes : (item.responseTypes?.length ? item.responseTypes : []),
            metadata: item.metadata ? {
              ...item.metadata,
              ...(useRootOnly ? { years: undefined } : {}),
            } : undefined,
          };
        })
    : [];

  // Subject codes to hide from the list filter because they belong to states
  // other than the individual teacher's selected state(s). Null (no scoping) for
  // school/stateless users; the teacher's own (non-system) tools are never hidden.
  // Derived from itemsForFilters — the SAME visibility/ownership-filtered items
  // that build the options — so an own tool that is filtered out of the current
  // view can't exempt a subject that only a visible system tool carries.
  const hiddenSubjectCodes = useMemo(
    () => computeHiddenSubjectCodes(apiSubjects, scopeStates, itemsForFilters),
    [apiSubjects, scopeStates, itemsForFilters],
  );

  // If a state change hides a subject that is currently selected in the filter,
  // drop it so the filter can't stay stuck on an out-of-scope subject.
  useEffect(() => {
    if (!hiddenSubjectCodes || !filterSelectedSubjects.length) return;
    const pruned = filterSelectedSubjects.filter(
      (c) => !hiddenSubjectCodes.has(String(c).toUpperCase()),
    );
    if (pruned.length !== filterSelectedSubjects.length) setFilterSelectedSubjects(pruned);
  }, [hiddenSubjectCodes, filterSelectedSubjects]);

  const hasSystemSelected = (() => {
    return selectedItemIds.some((id) => {
      const item = Array.isArray(itemsArray)
        ? itemsArray.find((element) => getItemId(element) === id)
        : null;

      return item?.isSystem;
    });
  })();

  const handleShareSelectedItems = () => {
    if (selectedItemIds.length === 0 || hasSystemSelected) {
      return;
    }

    // Determine shareType from context if available
    const contextShareType = context?.state?.context === 'commentBanks' ? 'commentBank' :
                            context?.state?.context === 'successCriteria' ? 'successCriteria' :
                            context?.state?.context === 'shortAnswers' ? 'shortAnswer' :
                            context?.state?.context === 'markingCriteria' ? 'markingCriteria' :
                            null;

    if (!contextShareType) {
      return;
    }

    const documents = selectedItemIds.map((id) => {
      // Try to find item with flexible ID matching (string vs number)
      const item = Array.isArray(itemsArray)
        ? itemsArray.find((element) => {
            const elementId = getItemId(element);
            return elementId === id || String(elementId) === String(id) || elementId?.toString() === id?.toString();
          })
        : null;

      // Use item's shareType if found, otherwise use context shareType
      const shareType = item ? getShareTypeForItem(item) : contextShareType;

      if (!id || !shareType) {
        return null;
      }

      const itemTitle = item ? getItemTitle(item) : '';
      const name = itemTitle || (
        shareType === 'commentBank' ? 'Comment Bank' :
        shareType === 'successCriteria' ? 'Success Criteria' :
        shareType === 'shortAnswer' ? 'Short Answer' :
        'Rubrics'
      );

      return {
        id: String(id), // Ensure id is a string
        shareType: String(shareType), // Ensure shareType is a string
        name: name || 'Untitled'
      };
    }).filter(Boolean);

    if (documents.length === 0) {
      return;
    }

    // Set both states together
    setShareItems(documents);
    setShowSharePopup(true);
  };

  const handleSharePopupClose = () => {
    setShowSharePopup(false);
    // Don't clear shareItems immediately - let it persist in case popup reopens
    // setShareItems([]);
  };

  const handleSharePopupSuccess = (people, successMessage) => {
    setShareSuccess({
      people,
      message: successMessage
    });
    handleSharePopupClose();
    
    const clearSelection = () => {
      if (setSelectedCriteria !== null) {
        setSelectedCriteria(new Set());
      } else if (context && context.deselectAll) {
        context.deselectAll();
      }
    };
    
    clearSelection();
  };

  const handleShareSuccessPopupClose = () => {
    setShareSuccess(null);
  };

  const handleDownloadSelectedItems = async () => {
    if (selectedItemIds.length === 0) {
      return;
    }

    try {
      if (selectedItemIds.length === 1) {
        const id = selectedItemIds[0];
        const item = Array.isArray(itemsArray)
          ? itemsArray.find((element) => getItemId(element) === id)
          : null;

        if (!item) {
          return;
        }

        const shareType = getShareTypeForItem(item);

        if (shareType === 'commentBank') {
          const extractedCommentBank = {
            title: item.title,
            smartComments: item.smartComments || [],
          };
          exportJsonFile(extractedCommentBank, item.title || 'Comment Bank', 'cb');
        } else if (shareType === 'markingCriteria') {
          const extractedMarkingCriteria = {
            title: item.title,
            type: 'RUBRICS',
            criterias: item.criterias || [],
          };
          exportJsonFile(extractedMarkingCriteria, item.title || 'Rubrics', 'mc');
        } else if (shareType === 'successCriteria') {
          const extractedSuccessCriteria = {
            title: item.title,
            criterias: item.criterias || [],
          };
          exportJsonFile(extractedSuccessCriteria, item.title || 'Success Criteria', 'sc');
        } else if (shareType === 'shortAnswer') {
          const extractedShortAnswer = {
            title: item.title,
            highestMarks: item.highestMarks,
            criterias: item.criterias || [],
          };
          exportJsonFile(extractedShortAnswer, item.title || 'Short Answer', 'sa');
        }
      } else {
        const filesToZip = [];

        for (const id of selectedItemIds) {
          const item = Array.isArray(itemsArray)
            ? itemsArray.find((element) => getItemId(element) === id)
            : null;

          if (!item) continue;

          const shareType = getShareTypeForItem(item);

          if (shareType === 'commentBank') {
            const extractedCommentBank = {
              title: item.title,
              smartComments: item.smartComments || [],
            };
            filesToZip.push({
              data: extractedCommentBank,
              filename: item.title || 'Comment Bank',
              extension: 'cb',
            });
          } else if (shareType === 'markingCriteria') {
            const extractedMarkingCriteria = {
              title: item.title,
              type: 'RUBRICS',
              criterias: item.criterias || [],
            };
            filesToZip.push({
              data: extractedMarkingCriteria,
              filename: item.title || 'Rubrics',
              extension: 'mc',
            });
          } else if (shareType === 'successCriteria') {
            const extractedSuccessCriteria = {
              title: item.title,
              criterias: item.criterias || [],
            };
            filesToZip.push({
              data: extractedSuccessCriteria,
              filename: item.title || 'Success Criteria',
              extension: 'sc',
            });
          } else if (shareType === 'shortAnswer') {
            const extractedShortAnswer = {
              title: item.title,
              highestMarks: item.highestMarks,
              criterias: item.criterias || [],
            };
            filesToZip.push({
              data: extractedShortAnswer,
              filename: item.title || 'Short Answer',
              extension: 'sa',
            });
          }
        }

        if (filesToZip.length === 0) {
          return;
        }

        const contextName = context?.state?.context === 'commentBanks'
          ? 'CommentBanks'
          : context?.state?.context === 'successCriteria'
          ? 'SuccessCriteria'
          : context?.state?.context === 'shortAnswers'
          ? 'ShortAnswers'
          : 'MarkingCriteria';
        const zipFilename = `${contextName}_${new Date().getTime()}`;

        await exportMultipleFilesAsZip(filesToZip, zipFilename);
      }
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  };

  const filteredItemsBySearch = (() => {
    const applyVisibilityFilter = (items) => {
      if (enableReactQuery && queries?.items?.data !== undefined && queries?.items?.data !== null && Array.isArray(items)) {
        return items.filter(item => {
          const isHidden = item.isVisible === false || item.visible === false;
          const isSystem = item.isSystem;

          if (showHidden) {
            if (item.isVisible !== false && item.visible !== false) {
              return false;
            }
          } else {
            if (item.isVisible === false || item.visible === false) {
              return false;
            }
          }

          if (showOnlyMineFilter && item.isSystem) {
            return false;
          }

          return true;
        });
      }
      return items;
    };

    const applySearchFilter = (items) => {
      if (searchValue && Array.isArray(items)) {
        return items.filter(item => {
          const title = getItemTitle(item);
          const matches = title.toLowerCase().includes(searchValue.toLowerCase());
          return matches;
        });
      }
      return items;
    };

    const hasApiFilters = enableReactQuery && context?.state?.queryParams &&
      ((context.state.queryParams.years?.length ?? 0) > 0 ||
       (context.state.queryParams.subjects?.length ?? 0) > 0 ||
       (context.state.queryParams.taskTypes?.length ?? 0) > 0 ||
       (context.state.queryParams.responseTypes?.length ?? 0) > 0);

    const visibilityFiltered = applyVisibilityFilter(itemsArray);
    const searchFiltered = applySearchFilter(visibilityFiltered);
    const subjectYearFiltered = applySubjectYearFilterUtil(searchFiltered, {
      context: context?.state?.context,
      filterSelectedYears: filterSelectedYears || [],
      filterSelectedSubjects: filterSelectedSubjects || [],
      filterSelectedTaskTypes: filterSelectedTaskTypes || [],
      filterSelectedResponseTypes: filterSelectedResponseTypes || [],
      hasApiFilters,
    });

    return subjectYearFiltered;
  })();

  const sortableItems = Array.isArray(filteredItemsBySearch) ? filteredItemsBySearch.map((item, index) => {
    const id = getItemId(item) || `item-${index}`;
    const sortOrder = getItemSortOrder(item);
    
    return {
      id: id.toString(),
      element: item,
      sortOrder: sortOrder
    };
  }) : [];

  const visibleItemIds = Array.isArray(filteredItemsBySearch) ? filteredItemsBySearch.map(item => {
    return getItemId(item);
  }).filter(id => id !== null) : [];

  const visibleItemsCount = visibleItemIds.length;
  const isAllSelected = visibleItemIds.length > 0 && visibleItemIds.every(id => selectedItems.has(id));

  const renderSortableItems = () => {
    if (!Array.isArray(sortableItems) || sortableItems.length === 0) {
      return null;
    }
    
    return sortableItems.map(({ id, element }) => {
      const isSelected = selectedItems.has(id);
      
      return (
        <SortableItem
          key={id?.toString() || ''}
          id={id?.toString() || ''}
          disabled={isDragDisabled || isSortActive}
          hasActiveFilters={hasInternalActiveFilters}
          hasSelectedItems={hasSelectedItems}
        >
          {typeof renderItem === 'function' ? renderItem(element, isSelected, selectedItems) : element}
        </SortableItem>
      );
    });
  };

  const isAnyMutationPending = mutations && typeof mutations === 'object' 
    ? Object.values(mutations).some(mutation => mutation?.isPending)
    : false;

  const handleBulkAction = (actionType) => {
    const selectedfeedbacktools = Array.from(selectedItems || []);
    
    const clearSelection = () => {
      if (externalSelectedItems && setSelectedCriteria) {
        setSelectedCriteria(new Set());
      } else if (context && context.deselectAll) {
        context.deselectAll();
      }
    };
    
    if (enableReactQuery && mutations) {
      switch (actionType) {
        case 'hide':
          if (mutations.hideItems) {
            mutations.hideItems.mutate({ selectedfeedbacktools });
            clearSelection();
          }
          break;
        case 'show':
          if (mutations.showItems) {
            mutations.showItems.mutate({ selectedfeedbacktools });
            clearSelection();
          }
          break;
        case 'delete':
          if (mutations.deleteItems) {
            const itemsToDelete = selectedfeedbacktools.filter(id => {
              const item = Array.isArray(itemsArray)
                ? itemsArray.find((element) => getItemId(element) === id)
                : allMarkingCriteria?.find(c => c.id === id);
              return item && !item.isSystem;
            });
            const systemItems = selectedfeedbacktools.filter(id => {
              const item = Array.isArray(itemsArray)
                ? itemsArray.find((element) => getItemId(element) === id)
                : allMarkingCriteria?.find(c => c.id === id);
              return item && item.isSystem;
            });

            mutations.deleteItems.mutate({ itemsToDelete, systemItems });
            clearSelection();
          }
          break;
        case 'moveToTop':
          if (mutations.moveToTop) {
            const validIds = selectedfeedbacktools.filter(id => id && id !== null && id !== undefined);
            
            if (validIds.length > 0) {
              try {
                mutations.moveToTop.mutate({ selectedfeedbacktools: validIds });
              } catch (error) {
                console.error('Error in handleBulkAction:', error);
              }
            }
            clearSelection();
          }
          break;
      }
    } else {
      switch (actionType) {
        case 'hide':
          onHide();
          break;
        case 'show':
          onShow();
          break;
        case 'delete':
          onDelete();
          break;
        case 'moveToTop':
          onMoveToTop();
          break;
      }
    }
  };

  const getCurrentSelectionState = () => {
    const currentVisibleItemIds = Array.isArray(filteredItemsBySearch) ? 
      filteredItemsBySearch.map(item => getItemId(item)).filter(id => id !== null) : [];


    const currentSelectedItems = selectedItems || new Set();
    const isAllVisibleSelected = currentVisibleItemIds.length > 0 && 
                                currentVisibleItemIds.every(id => currentSelectedItems.has(id));

    return {
      currentVisibleItemIds,
      isAllVisibleSelected,
      visibleItemsCount: currentVisibleItemIds.length
    };
  };

  const handleSelectAll = () => {
     if (setSelectedCriteria !== null) {
       const { currentVisibleItemIds, isAllVisibleSelected } = getCurrentSelectionState();

       if (isAllVisibleSelected) {
         setSelectedCriteria(new Set());
       } else {
         setSelectedCriteria(new Set(currentVisibleItemIds));
       }
       return;
     }
     
     if (enableReactQuery && context) {
       const { currentVisibleItemIds, isAllVisibleSelected } = getCurrentSelectionState();

       if (isAllVisibleSelected) {
         context.deselectAll();
       } else {
         context.selectAll(currentVisibleItemIds);
       }
       return;
     }

    if (externalSelectedItems !== undefined && typeof onSelectAll === 'function') {
      onSelectAll();
    }
  };

  const isItemsLoading = enableReactQuery && queries?.items?.isLoading;
  const isItemsFetching = enableReactQuery && queries?.items?.isFetching;
  const hasItemsData = enableReactQuery && queries?.items?.data !== undefined && queries?.items?.data !== null;
  const showListLoader = enableReactQuery && (isItemsLoading || !hasItemsData);

  try {
    return (
      <MainContainer>
        {renderImportDialog && renderImportDialog()}
        {SecondarySidebar && (
          <SecondarySidebar
            isShowMenu={isShowMenu}
            setShowMenu={setShowMenu}
          />
        )}
        <InnerContainer>
          {tabletView && MenuButton && (
            <HeadingAndFilterContainer>
              <MenuButton setShowMenu={setShowMenu} />
            </HeadingAndFilterContainer>
          )}
          <RightContainer>
            <RightSideHeader>
              {!hideCreateButton && (
                <CreateButtonContainer
                  onClick={handleCreateButtonClick}
                  selected={openCreateDialog}
                >
                  <PlusIconHover src={PlusWhiteIcon} alt="" />
                  <PlusText>{getCreateButtonText()}</PlusText>
                  {openCreateDialog && !usePopupForCreate && (
                    <PopUpContainer ref={divRef}>
                      {typeof renderCreateOptions === 'function' ? renderCreateOptions() : null}
                    </PopUpContainer>
                  )}
                </CreateButtonContainer>
              )}
              <RightSideOptions>
                <SearchContainer data-search-container>
                  <SearchInputWrapper>
                    <SearchItems
                      placeholderText="Search"
                      searchValue={searchValue}
                      onSearch={handleSearchChange}
                    />
                    <SearchIconImg src={SearchIcon} />
                  </SearchInputWrapper>
                </SearchContainer>
                <NoBgRoundedBlackBorderLight
                  text="Only Hidden"
                  onclick={() => handleShowHiddenChange(!showHidden)}
                />
                <NoBgRoundedBlackBorderLight
                  text="Only Mine"
                  onclick={() => handleShowOnlyMineChange(!showOnlyMineFilter)}
                />
                <SubjectFilter
                  selectedYears={filterSelectedYears}
                  setSelectedYears={setFilterSelectedYears}
                  selectedSubjects={filterSelectedSubjects}
                  setSelectedSubjects={setFilterSelectedSubjects}
                  selectedTaskTypes={filterSelectedTaskTypes}
                  setSelectedTaskTypes={setFilterSelectedTaskTypes}
                  selectedResponseTypes={filterSelectedResponseTypes}
                  setSelectedResponseTypes={setFilterSelectedResponseTypes}
                  items={itemsArray}
                  itemsForFilterOptions={itemsForFilters}
                  hiddenSubjectCodes={hiddenSubjectCodes}
                />
                {enableReactQuery && context && (
                  <SortByControl
                    value={sortByUiValue}
                    onChange={handleSortByChange}
                  />
                )}
                {!hideCreateButton && (
                  <ImportFileLabel>
                    <UploadIcon src={UploadIconSrc} />
                    <CardTitle>Upload</CardTitle>
                    <ImportFile
                      type="file"
                      accept={getAcceptFileType()}
                      onChange={onImport}
                    />
                  </ImportFileLabel>
                )}
              </RightSideOptions>
            </RightSideHeader>

            {context?.state?.context === 'markingCriteria' && (
              <SharedWithMeLink 
                resourceTypes={['MARKING_TEMPLATE']} 
                link="#/sharedmarkingtools"
              />
            )}
            {context?.state?.context === 'commentBanks' && (
              <SharedWithMeLink 
                resourceTypes={['COMMENT_BANK', 'COMMENT_BANKS']} 
                link="#/sharedmarkingtools"
              />
            )}
            {context?.state?.context === 'successCriteria' && (
              <SharedWithMeLink
                resourceTypes={['SUCCESS_CRITERIA']}
                link="#/sharedmarkingtools"
              />
            )}
            {context?.state?.context === 'shortAnswers' && (
              <SharedWithMeLink
                resourceTypes={['SHORT_ANSWERS']}
                link="#/sharedmarkingtools"
              />
            )}

            <BulkActionsContainer>
              {selectedItems && selectedItems.size > 0 && (
                <SelectedCountText>
                  {selectedItems.size} selected:
                </SelectedCountText>
              )}

              {!showHidden && (
                <BulkActionButton
                  onClick={() => handleBulkAction('moveToTop')}
                  disabled={isAnyMutationPending || !selectedItems || selectedItems.size === 0}
                >
                  <BulkActionIcon src={TopIcon} alt="Move to Top" />
                  {mutations?.moveToTop?.isPending ? 'Moving...' : 'Move To Top'}
                </BulkActionButton>
              )}

              {showHidden ? (
                <BulkActionButton
                  onClick={() => handleBulkAction('show')}
                  disabled={isAnyMutationPending || !selectedItems || selectedItems.size === 0}
                >
                  <BulkActionIcon src={HideIcon} alt="Show" />
                  {mutations?.showItems?.isPending ? 'Unhiding...' : 'Unhide'}
                </BulkActionButton>
              ) : (
                <BulkActionButton
                  onClick={() => handleBulkAction('hide')}
                  disabled={isAnyMutationPending || !selectedItems || selectedItems.size === 0}
                >
                  <BulkActionIcon src={HideIcon} alt="Hide" />
                  {mutations?.hideItems?.isPending ? 'Hiding...' : 'Hide'}
                </BulkActionButton>
              )}
              
              <BulkActionButton
                onClick={handleShareSelectedItems}
                disabled={
                  isAnyMutationPending ||
                  selectedItemIds.length === 0 ||
                  hasSystemSelected
                }
              >
                <BulkActionIcon src={ShareBulkIcon} alt="Share" />
                Share
              </BulkActionButton>

              <BulkActionButton
                onClick={handleDownloadSelectedItems}
                disabled={
                  isAnyMutationPending ||
                  selectedItemIds.length === 0
                }
              >
                <BulkActionIcon src={DownloadIcon} alt="Download" />
                Download
              </BulkActionButton>

              {!hideCreateButton && (
                <BulkActionButton
                  onClick={() => handleBulkAction('delete')}
                  disabled={isAnyMutationPending || !selectedItems || selectedItems.size === 0}
                >
                  <BulkActionIcon src={DeleteIcon} alt="Delete" />
                  {mutations?.deleteItems?.isPending ? 'Deleting...' : 'Delete'}
                </BulkActionButton>
              )}
              
              {(() => {
                const selectionState = getCurrentSelectionState();
                
                return (
                  <BulkActionButton
                    onClick={handleSelectAll}
                    disabled={
                      isAnyMutationPending || !Array.isArray(filteredItemsBySearch) || selectionState.visibleItemsCount === 0
                    }
                  >
                    <BulkActionIcon
                      src={SelectAllIcon}
                      alt={selectionState.isAllVisibleSelected ? 'Deselect All' : 'Select All'}
                    />
                    {selectionState.isAllVisibleSelected ? 'Deselect all' : 'Select all'}
                  </BulkActionButton>
                );
              })()}
            </BulkActionsContainer>

            <ListAreaWrapper>
              {showListLoader && (
                <ListLoaderOverlay>
                  <lottie-player
                    autoplay
                    loop
                    mode="normal"
                    src="./icons/loader.json"
                  />
                  <ListLoaderText>Loading brain power...</ListLoaderText>
                </ListLoaderOverlay>
              )}
              {customContent ? (
                <MarkingCriteriaList>
                  <ScrollableArea ref={scrollContainerRef} style={{ width: '100%', height: '100%' }}>
                    {customContent}
                  </ScrollableArea>
                </MarkingCriteriaList>
              ) : (
                <MarkingCriteriaList>
                  <ScrollableArea ref={scrollContainerRef} style={{ width: '100%', height: '100%' }}>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={Array.isArray(sortableItems) ? sortableItems.map((item) => item.id?.toString() || '') : []}
                        strategy={verticalListSortingStrategy}
                      >
                        {renderSortableItems()}
                      </SortableContext>
                    </DndContext>
                  </ScrollableArea>
                </MarkingCriteriaList>
              )}
            </ListAreaWrapper>
          </RightContainer>
        </InnerContainer>
        {showCreatePopup && usePopupForCreate && renderCreatePopup && (
          renderCreatePopup({ onClose: () => setShowCreatePopup(false) })
        )}
        {showSharePopup && (
          <ShareResourcePopup
            open={showSharePopup}
            onClose={handleSharePopupClose}
            items={shareItems}
            onShare={handleSharePopupSuccess}
          />
        )}
        {shareSuccess && (
          <ActionPopup
            open={!!shareSuccess}
            onClose={handleShareSuccessPopupClose}
            type="share"
            message={shareSuccess.message}
            emailList={shareSuccess.people}
            primaryButtonLabel="Awesome!"
            primaryButtonAction={handleShareSuccessPopupClose}
          />
        )}
      </MainContainer>
    );
  } catch (error) {
    console.error('Error in FeedbackTool:', error);
    return <div>Error</div>;
  }
}

export default FeedbackTool;
